import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getMe, logout as apiLogout } from '@/lib/api';
import translations from '@/lib/i18n';

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
};

export const useTranslation = () => {
  const { language } = useAuth();
  const t = useCallback((key) => {
    return translations[language]?.[key] || translations['en']?.[key] || key;
  }, [language]);
  return { t, language };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [language, setLanguage] = useState(() => localStorage.getItem('lang') || 'fr');
  const [families, setFamilies] = useState([]);

  const checkAuth = useCallback(async () => {
    try {
      const res = await getMe();
      setUser(res.data);
      if (res.data.language) {
        setLanguage(res.data.language);
        localStorage.setItem('lang', res.data.language);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const handleLogout = async () => {
    try { await apiLogout(); } catch {}
    setUser(null);
    setFamilies([]);
  };

  const switchLanguage = (lang) => {
    setLanguage(lang);
    localStorage.setItem('lang', lang);
  };

  return (
    <AuthContext.Provider value={{
      user, setUser, loading, language, switchLanguage,
      families, setFamilies, logout: handleLogout, checkAuth
    }}>
      {children}
    </AuthContext.Provider>
  );
};
